import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import type { TeachingEvidenceModel } from "@/api/cle-api";
import {
  mergeEvidenceDetailUpdate,
  mergeEvidenceDetailWithOverride,
  useDownloadTeaching,
  useReclassifyReviewThread,
  useTeaching,
  useTeachingPdf,
} from "@/hooks/cle-hooks";
import { showCleApiErrorToast } from "@/lib/showCleApiErrorToast";
import { useEffect, useMemo, useState } from "react";
import { useToaster } from "rsuite";

import {
  getReclassifyLocks,
  getMissingEvidenceFields,
} from "../person/id/Efteruddannelse/utils/approvalCompleteness";
import {
  DocumentClassificationActions,
  REPORTED_EVIDENCE_LOCKED_REASON,
} from "../person/id/Efteruddannelse/DocumentClassificationActions";
import { EvidencePdfViewer } from "../person/id/Efteruddannelse/EvidencePdfViewer";
import type { CourseCertificateDecision } from "../person/id/Efteruddannelse/types";
import { StatusBadge } from "../person/id/Efteruddannelse/StatusBadge";
import { SupplementaryDocuments } from "../person/id/Efteruddannelse/SupplementaryDocuments";
import {
  formatEvidenceStatus,
  getEvidenceStatusVariant,
} from "../person/id/Efteruddannelse/utils/evidenceStatus";
import { formatDate } from "../person/id/Efteruddannelse/utils/formatters";
import { useObjectUrl } from "../person/id/Efteruddannelse/useObjectUrl";
import {
  DocumentAuditTrailPanel,
  DocumentCommentsPanel,
} from "./DocumentReviewPanels";
import { EvidenceReviewThreadTopPanel } from "./EvidenceReviewThreadTopPanel";
import { ReviewDetailLayout } from "./ReviewDetailLayout";
import { ReviewInfoCard, ReviewInfoField } from "./ReviewInfoCard";
import { TeachingValidation } from "./TeachingValidation";

interface TeachingReviewViewProps {
  userId: string;
  teachingId: string;
  onBack: () => void;
}

export const TeachingReviewView = ({
  userId,
  teachingId,
  onBack,
}: TeachingReviewViewProps) => {
  const { data, isLoading, error } = useTeaching({ userId, teachingId });
  const { data: pdfBlob, isLoading: isPdfLoading } = useTeachingPdf({
    userId,
    teachingId,
  });
  const toaster = useToaster();
  const { mutate: downloadTeaching, isPending: isDownloading } =
    useDownloadTeaching();
  const { mutate: reclassifyTeaching, isPending: isReclassifying } =
    useReclassifyReviewThread();
  const pdfUrl = useObjectUrl(pdfBlob);
  const [teachingOverride, setTeachingOverride] = useState<
    TeachingEvidenceModel | undefined
  >(undefined);

  useEffect(() => {
    setTeachingOverride(undefined);
  }, [teachingId]);

  const teaching = useMemo(
    () => mergeEvidenceDetailWithOverride(data, teachingOverride),
    [data, teachingOverride],
  );

  const handleTeachingUpdated = (updated: TeachingEvidenceModel) => {
    setTeachingOverride((prev) =>
      mergeEvidenceDetailUpdate(data ?? prev, updated),
    );
  };

  const handleReclassify = (decision: CourseCertificateDecision) => {
    reclassifyTeaching(
      {
        subjectId: teachingId,
        userId,
        newStatus: decision,
      },
      {
        onSuccess: () => setTeachingOverride(undefined),
        onError: (err) => showCleApiErrorToast(toaster, err),
      },
    );
  };

  return (
    <ReviewDetailLayout
      className="p-2"
      onBack={onBack}
      topPanel={
        <EvidenceReviewThreadTopPanel
          reviewThread={teaching?.reviewThread}
          ownerUserId={userId}
        />
      }
      loading={isLoading}
      error={
        error ? (
          <CleApiErrorText
            error={error}
            fallback="Kunne ikke hente undervisning."
          />
        ) : null
      }
      pdfPanel={
        teaching ? (
          <EvidencePdfViewer
            fileName={teaching.fileName ?? "Undervisning"}
            isDownloading={isDownloading}
            isLoading={isPdfLoading}
            onDownload={() => downloadTeaching({ userId, teachingId })}
            pdfUrl={pdfUrl}
          />
        ) : null
      }
      auditPanel={<DocumentAuditTrailPanel subjectId={teachingId} />}
      infoPanel={
        teaching ? (
          <>
            <ReviewInfoCard title="Undervisningsdokument">
              <ReviewInfoField
                label="Filnavn"
                value={teaching.fileName ?? "-"}
              />
              <ReviewInfoField
                label="Status"
                value={
                  <StatusBadge
                    pill
                    status={formatEvidenceStatus(teaching.status)}
                    variant={getEvidenceStatusVariant(teaching.status)}
                  />
                }
              />
              <ReviewInfoField
                label="Indberettet"
                value={teaching.isReported ? "Ja" : "Nej"}
              />
              <ReviewInfoField
                label="Oprettet"
                value={formatDate(teaching.createdAt)}
              />
              <ReviewInfoField
                label="Oprettet af"
                value={teaching.createdByUserName ?? "-"}
              />
            </ReviewInfoCard>
            <TeachingValidation
              teaching={teaching}
              onTeachingUpdated={handleTeachingUpdated}
            />
            <SupplementaryDocuments
              evidence={teaching}
              kind="undervisning"
              userId={userId}
            />
          </>
        ) : null
      }
      actionsPanel={
        teaching ? (
          <DocumentClassificationActions
            isPending={isReclassifying}
            onReclassify={handleReclassify}
            {...getReclassifyLocks({
              isReported: teaching.isReported,
              missingFields: getMissingEvidenceFields(teaching),
              reportedReason: REPORTED_EVIDENCE_LOCKED_REASON,
            })}
          />
        ) : null
      }
      commentsPanel={
        <DocumentCommentsPanel
          subjectId={teachingId}
          ownerUserId={userId}
          onCommentAdded={() => setTeachingOverride(undefined)}
          isReported={teaching?.isReported ?? false}
        />
      }
    />
  );
};
